import { ParsedUrlQuery } from 'querystring'

import React from 'react'
import Head from 'next/head'
import {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
  NextPage,
} from 'next'

import { SitemapPage, SitemapSection } from 'components/Sitemap'
import ResponsivePageLayout from 'components/Layout/ResponsivePageLayout'
import { Breadcrumb } from 'components/Sitemap/SitemapBreadcrumbs'

import { fetchFooterData, fetchGlobalOptionsData } from 'utils/staticData'
import { browseLocationLandingIndex } from 'utils/locationLanding/api/fetching/browseLocationLandingIndex'
import {
  getLocationLandingSitemapLinks,
  getStateNameFromLocationDataByParam,
} from 'utils/sitemap'

import { SITEMAP_ROOT_PATH, SITEMAP_US_PATH } from 'constants/paths'

interface SitemapStateParams extends ParsedUrlQuery {
  state: string
}

export const getStaticPaths: GetStaticPaths<SitemapStateParams> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const footerData = await fetchFooterData()
  const globalOptionsData = await fetchGlobalOptionsData()
  const { state } = params as SitemapStateParams

  const pageFooterData = {
    menus: footerData,
    options: globalOptionsData.acf.global_footer_group,
  }

  const sitemapLocationData = await browseLocationLandingIndex({
    returnType: 'SitemapStateLocationData',
    attributesToRetrieve: ['objectID', 'cityData', 'stateData'],
    batchMapCb: (hit) => hit,
  })

  const stateName = getStateNameFromLocationDataByParam(
    sitemapLocationData,
    state,
  )

  const sitemapLinks = getLocationLandingSitemapLinks(
    sitemapLocationData,
    state,
  )

  if (!stateName) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      footerData: pageFooterData,
      sitemapLinks,
      stateName,
      stateAbbreviation: state,
    },
  }
}

const StatePage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  footerData,
  sitemapLinks,
  stateName,
  stateAbbreviation,
}) => {
  const breadcrumbs: Breadcrumb[] = [
    { text: 'Destinations', href: SITEMAP_ROOT_PATH },
    {
      text: stateName,
      href: `${SITEMAP_US_PATH}/${stateAbbreviation}`,
    },
  ]

  const meta = {
    title: `${stateName} Vacation Rental Destinations | Evolve Sitemap`,
    description: `Explore a list of links to all of our vacation rental destinations in ${stateName}`,
  }

  const canonicalUrl = `${process.env.NEXT_PUBLIC_ORIGIN}/sitemap/vacation-rentals/us/${stateAbbreviation}`

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta content={meta.title} property="og:title" />
        <meta content={meta.title} name="twitter:title" />
        <meta content={meta.description} name="description" />
        <meta content={meta.description} property="og:description" />
        <meta content={meta.description} name="twitter:description" />
        <link href={canonicalUrl} rel="canonical" />
      </Head>
      <ResponsivePageLayout
        footerData={footerData}
        headerData={{
          hideSearchBox: true,
        }}
      >
        {stateName && (
          <SitemapPage breadcrumbs={breadcrumbs} title={stateName}>
            <SitemapSection links={sitemapLinks} />
          </SitemapPage>
        )}
      </ResponsivePageLayout>
    </>
  )
}

export default StatePage
